import { Grid, Stack } from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { FormGroup as FormGroupUI } from "@tfg/ui"
import { useState } from "react"

import { FormRow } from "~/pages/Admin/components/FormRow"
import { RenderField } from "~/pages/Admin/components/RenderField"
import {
  FormFieldConfig,
  FormRowConfig,
  FormGroupConfig,
} from "~/pages/Admin/components/form.types"

export const FormGroup = <T extends Record<string, unknown>>({
  groupConfig,
  form,
}: {
  groupConfig: FormGroupConfig
  form: UseFormReturnType<T>
}) => {
  const [collapsed, setCollapsed] = useState(
    groupConfig.defaultCollapsed || false
  )

  const handleToggle = () => {
    if (groupConfig.collapsible) {
      setCollapsed(!collapsed)
    }
  }

  const isGroupDisabled = groupConfig.isDisabled?.(form.values) ?? false

  const renderElement = (
    element: FormFieldConfig | FormRowConfig,
    index: number
  ) => {
    if (element.type === "row") {
      const rowWithDisabledFields = isGroupDisabled
        ? {
            ...element,
            fields: element.fields.map((field) => ({
              ...field,
              disabled: field.disabled || isGroupDisabled,
            })),
          }
        : element

      return (
        <FormRow key={index} rowConfig={rowWithDisabledFields} form={form} />
      )
    }

    const field = isGroupDisabled
      ? { ...element, disabled: element.disabled || isGroupDisabled }
      : element

    return (
      <Grid.Col key={index} span={field.span || 12}>
        <RenderField field={field} form={form} />
      </Grid.Col>
    )
  }

  return (
    <FormGroupUI
      title={groupConfig.title}
      description={groupConfig.description}
      p="sm"
      w="100%"
      my="sm"
      style={{
        backgroundColor:
          groupConfig.variant === "filled"
            ? "var(--mantine-color-gray-0)"
            : "transparent",
      }}
      collapsible={groupConfig.collapsible}
      toggleCollapse={handleToggle}
      collapsed={collapsed}
    >
      {!collapsed && (
        <Stack gap={groupConfig.spacing || "md"} w="100%" p="xs">
          <Grid w="100%">{groupConfig.fields.map(renderElement)}</Grid>
        </Stack>
      )}
    </FormGroupUI>
  )
}
